/**
 * SidebarFolderItem
 *
 * Recursive folder tree row inside the SlideSense sidebar. Surfaces the folder
 * actions kebab (rename / delete) for admins. Extracted from SlideSenseSidebar
 * so the parent file stays under the max-lines cap.
 *
 * Reordering folders happens in the Admin → File Manager view, not here —
 * the sidebar is a read-only browse surface for everyone.
 */

import { useTranslation } from "react-i18next"

import { ChevronDown, ChevronRight, Folder, FolderOpen } from "@/components/icons"
import { SidebarMenuButton, SidebarMenuItem, SidebarMenuSub } from "@/components/ui/sidebar"

import { FolderActionsMenu } from "./FolderActionsMenu"
import { type FolderNode, isDescendantOf } from "../utils/folder-tree"

export interface SidebarFolderItemProps {
  node: FolderNode
  selectedFolderId: string | null
  onSelectFolder: (id: string | null) => void
  expandedIds: Set<string>
  onToggleExpand: (id: string) => void
  parentMap: Map<string, string | null>
  isCollapsed: boolean
  isAdmin: boolean
  onFolderDeleted: (folderId: string) => void
}

export function SidebarFolderItem({
  node,
  selectedFolderId,
  onSelectFolder,
  expandedIds,
  onToggleExpand,
  parentMap,
  isCollapsed,
  isAdmin,
  onFolderDeleted,
}: SidebarFolderItemProps) {
  const { t } = useTranslation()
  const { folder, children } = node
  const isSelected = selectedFolderId === folder.id
  const isExpanded = expandedIds.has(folder.id)
  const hasChildren = children.length > 0
  const hasSelectedDescendant =
    selectedFolderId !== null && isDescendantOf(selectedFolderId, folder.id, parentMap)
  const showOpen = isSelected || hasSelectedDescendant

  if (isCollapsed) return null

  return (
    <SidebarMenuItem>
      <SidebarMenuButton
        className="gap-2 pr-7"
        isActive={isSelected}
        onClick={() => onSelectFolder(folder.id)}
      >
        {hasChildren ? (
          // Span-as-button to avoid `<button> cannot contain a nested <button>`
          // — SidebarMenuButton already renders a <button>. role=button +
          // tabIndex=0 + onKeyDown gives keyboard users a way to expand
          // without triggering the parent's folder-select. Pointer events are
          // stopPropagation'd so a mouse click on the chevron only toggles
          // expansion.
          <span
            aria-expanded={isExpanded}
            aria-label={t(isExpanded ? "slideSense.collapseFolder" : "slideSense.expandFolder")}
            className="hover:bg-muted/40 focus-visible:ring-ring flex shrink-0 cursor-pointer items-center justify-center rounded-sm focus-visible:ring-1 focus-visible:outline-none"
            role="button"
            tabIndex={0}
            onPointerDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation()
              onToggleExpand(folder.id)
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                e.stopPropagation()
                onToggleExpand(folder.id)
              }
            }}
          >
            {isExpanded ? (
              <ChevronDown className="h-3.5 w-3.5" />
            ) : (
              <ChevronRight className="h-3.5 w-3.5" />
            )}
          </span>
        ) : (
          <span className="w-3.5 shrink-0" />
        )}
        {showOpen ? (
          <FolderOpen className="text-foreground/70 h-4 w-4 shrink-0" />
        ) : (
          <Folder className="text-foreground/70 h-4 w-4 shrink-0" />
        )}
        <span className="truncate">{folder.name}</span>
      </SidebarMenuButton>

      {isAdmin && (
        <div
          className="absolute top-1/2 right-1 -translate-y-1/2"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <FolderActionsMenu folder={folder} onDeleted={onFolderDeleted} />
        </div>
      )}

      {hasChildren && isExpanded && (
        <SidebarMenuSub>
          {children.map((child) => (
            <SidebarFolderItem
              key={child.folder.id}
              expandedIds={expandedIds}
              isAdmin={isAdmin}
              isCollapsed={isCollapsed}
              node={child}
              parentMap={parentMap}
              selectedFolderId={selectedFolderId}
              onFolderDeleted={onFolderDeleted}
              onSelectFolder={onSelectFolder}
              onToggleExpand={onToggleExpand}
            />
          ))}
        </SidebarMenuSub>
      )}
    </SidebarMenuItem>
  )
}
